import {useCallback, useMemo} from 'react';

import {UserSettingsStore} from '@udemy/common-stores';
import {useStores} from '@udemy/store-provider';

import {FREQUENCY_CAP_MS, LONG_SNOOZE_DAYS, MS_PER_DAY} from './constants';

const POP_QUIZ_SETTINGS = Object.freeze({
    /** ISO date when the pop quiz was last shown. Used for the frequency cap. */
    lastShownDate: Object.freeze({
        settingCode: 'pp-pop-quiz_last-shown-date',
        defaultValue: '',
    }),
    /** ID of the last quiz shown. Used for quiz rotation (avoid repeating). */
    lastQuizId: Object.freeze({
        settingCode: 'pp-pop-quiz_last-quiz-id',
        defaultValue: '',
    }),
    /**
     * ISO date when the user requested the 60-day snooze.
     * Expiry is computed on read: snoozedAt + LONG_SNOOZE_DAYS > today.
     * Storing the request date (not the expiry) keeps the value portable —
     * if the snooze duration changes, old records still reflect user intent.
     */
    snoozedAt: Object.freeze({
        settingCode: 'pp-pop-quiz_snoozed-at',
        defaultValue: '',
    }),
});

type PopQuizSetting = (typeof POP_QUIZ_SETTINGS)[keyof typeof POP_QUIZ_SETTINGS];

/** Shape of the JSON blob stored under `pp-pop-quiz_settings`. */
interface PopQuizSettingsPayload {
    lastShownDate: string;
    lastQuizId: string;
    snoozedAt: string;
}

const EMPTY_PAYLOAD: PopQuizSettingsPayload = {lastShownDate: '', lastQuizId: '', snoozedAt: ''};

function parsePayload(raw: string | undefined): PopQuizSettingsPayload {
    if (!raw) {
        return {...EMPTY_PAYLOAD};
    }
    try {
        return JSON.parse(raw) as PopQuizSettingsPayload;
    } catch {
        return {...EMPTY_PAYLOAD};
    }
}

function payloadKey(setting: PopQuizSetting): keyof PopQuizSettingsPayload {
    switch (setting.settingCode) {
        case POP_QUIZ_SETTINGS.lastShownDate.settingCode:
            return 'lastShownDate';
        case POP_QUIZ_SETTINGS.lastQuizId.settingCode:
            return 'lastQuizId';
        default:
            return 'snoozedAt';
    }
}

/**
 * True when the pop quiz is allowed to be shown right now — i.e. neither the long snooze nor
 * the frequency cap is currently active.
 */
function computeIsEligible(getSetting: (setting: PopQuizSetting) => string): boolean {
    if (typeof window === 'undefined') {
        // Settings are not available server-side — treat as ineligible so the query stays
        // client-only.
        return false;
    }
    // Long snooze takes priority: check if snoozedAt + 60 days is still in the future.
    const snoozedAt = getSetting(POP_QUIZ_SETTINGS.snoozedAt);
    if (snoozedAt) {
        const snoozeExpiresAt = new Date(snoozedAt).getTime() + LONG_SNOOZE_DAYS * MS_PER_DAY;
        if (Date.now() < snoozeExpiresAt) {
            return false;
        }
    }
    // Fall through to the frequency cap.
    const lastShown = getSetting(POP_QUIZ_SETTINGS.lastShownDate);
    if (!lastShown) {
        return true;
    }
    return Date.now() - new Date(lastShown).getTime() >= FREQUENCY_CAP_MS;
}

export interface PopQuizEligibility {
    isEligible: boolean;
    markShownToday: () => void;
    markQuizOffered: (quizId: number) => void;
    markQuizDismissed: (quizId: number) => void;
    markSnoozed: () => void;
    wasQuizRecentlyOffered: (quizId: string) => boolean;
}

/**
 * Hook providing the single owning interface for Pop Quiz eligibility. Instantiate once (in
 * `usePopQuiz()`) and pass the returned object down to both the orchestrator and the lifecycle
 * hook — callers never need to know about the underlying storage keys.
 */
export function usePopQuizEligibility(): PopQuizEligibility {
    const [userSettingsStore] = useStores([UserSettingsStore]);

    const getSetting = useCallback(
        (setting: PopQuizSetting): string => {
            const raw = userSettingsStore.get(UserSettingsStore.SETTINGS.popQuizSettings) as
                | string
                | undefined;
            const payload = parsePayload(raw);
            return payload[payloadKey(setting)] ?? setting.defaultValue;
        },
        [userSettingsStore],
    );

    const setSetting = useCallback(
        async (setting: PopQuizSetting, value: string): Promise<void> => {
            const raw = userSettingsStore.get(UserSettingsStore.SETTINGS.popQuizSettings) as
                | string
                | undefined;
            const payload = parsePayload(raw);
            payload[payloadKey(setting)] = value;
            await userSettingsStore.set(
                UserSettingsStore.SETTINGS.popQuizSettings,
                JSON.stringify(payload),
            );
        },
        [userSettingsStore],
    );

    const markShownToday = useCallback(() => {
        void setSetting(POP_QUIZ_SETTINGS.lastShownDate, new Date().toISOString().slice(0, 10));
    }, [setSetting]);

    const markQuizOffered = useCallback(
        (quizId: number) => {
            void setSetting(POP_QUIZ_SETTINGS.lastQuizId, String(quizId));
        },
        [setSetting],
    );

    const markQuizDismissed = useCallback(
        (quizId: number) => {
            markQuizOffered(quizId);
            markShownToday();
        },
        [markQuizOffered, markShownToday],
    );

    const markSnoozed = useCallback(() => {
        void setSetting(POP_QUIZ_SETTINGS.snoozedAt, new Date().toISOString().slice(0, 10));
    }, [setSetting]);

    const wasQuizRecentlyOffered = useCallback(
        (quizId: string) => getSetting(POP_QUIZ_SETTINGS.lastQuizId) === quizId,
        [getSetting],
    );

    const isEligible = computeIsEligible(getSetting);

    return useMemo(
        () => ({
            isEligible,
            markShownToday,
            markQuizOffered,
            markQuizDismissed,
            markSnoozed,
            wasQuizRecentlyOffered,
        }),
        [
            isEligible,
            markShownToday,
            markQuizOffered,
            markQuizDismissed,
            markSnoozed,
            wasQuizRecentlyOffered,
        ],
    );
}
